import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ROOMS, SITE } from "@/data/site";

export default function Rooms() {
  const waHref = `https://wa.me/${SITE.phoneE164}?text=${encodeURIComponent(
    SITE.whatsappMessage
  )}`;

  return (
    <section
      id="rooms"
      className="relative bg-[var(--pam-forest)] text-white"
      style={{ padding: "5% 0" }}
      data-testid="rooms-section"
    >
      <div className="container-pam">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: [0.2, 0.9, 0.2, 1] }}
              className="overline text-[var(--pam-gold)]"
            >
              Stays
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.9,
                delay: 0.1,
                ease: [0.2, 0.9, 0.2, 1],
              }}
              className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl"
            >
              Authentic Malnad house{" "}
              <span className="relative inline-block">
                <span className="italic text-[var(--pam-gold)] relative z-10">
                  cottages.
                </span>
                {/* animated underline that draws after headline settles */}
                <motion.span
                  aria-hidden="true"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{
                    duration: 1.1,
                    delay: 0.9,
                    ease: [0.2, 0.9, 0.2, 1],
                  }}
                  style={{ transformOrigin: "left center" }}
                  className="absolute left-0 right-0 -bottom-1 md:-bottom-2 h-[6px] bg-[var(--pam-gold)]/45 rounded-sm"
                />
              </span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-md text-white/70 font-light leading-relaxed"
          >
            Clay-tiled roofs, wide verandahs and forest-facing windows —
            every cottage carries the soul of a real Malnad home.
          </motion.p>
        </div>

        <div className="rooms-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="rooms-grid">
          {ROOMS.map((r, i) => (
            <motion.a
              key={r.id}
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.9,
                delay: i * 0.1,
                ease: [0.2, 0.9, 0.2, 1],
              }}
              className="room-card group relative overflow-hidden rounded-2xl border border-white/10 block ring-1 ring-white/5"
              style={{ willChange: "transform, opacity" }}
              data-testid={`room-card-${r.id}`}
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src={r.img}
                  alt={`${r.name} at Peak Adventures Malnad`}
                  loading="lazy"
                  className="room-card-img w-full h-full object-cover"
                  style={{ willChange: "transform" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1612]/95 via-[#0A1612]/20 to-transparent" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="overline text-[var(--pam-gold)]">{r.tag}</p>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <h3 className="font-serif text-2xl md:text-[28px]">
                    {r.name}
                  </h3>
                  <div className="w-10 h-10 rounded-full border border-white/40 grid place-items-center group-hover:bg-[var(--pam-gold)] group-hover:border-[var(--pam-gold)] group-hover:text-[var(--pam-forest)] transition-all duration-300 shrink-0">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
